import { useMemo, useState } from "react";
import { deriveLedger, makeEconomy, fuelLogsForCar, type CanonicalStore, type Car, type Spending, type FuelLog, type TyreSet, type Tyre } from "../../core/index.ts";
import { SplitFlapNumber } from "../components/SplitFlapNumber.tsx";
import { SpendingForm } from "../forms/SpendingForm.tsx";
import { FuelLogForm } from "../forms/FuelLogForm.tsx";
import { TyreSetForm, TyreForm } from "./TyresView.tsx";
import { FillupsView } from "./FillupsView.tsx";
import { PeriodFilter, extractPeriods, filterByPeriod } from "./InsightsView.tsx";
import { BackRow } from "../forms/Modal.tsx";
import { longDate, round0 } from "../format.ts";

const isFuelCategory = (c: string) => c === "Fuel" || c === "Charging";

type LedgerEntry = ReturnType<typeof deriveLedger>["entries"][number];

/**
 * Category tint. Same assignment as iOS's `SpendingTrackerView.color(for:)`, so a
 * category is the same colour whichever app you open. Falls back to muted for a
 * category the map doesn't know, which is also what the mobile switch does.
 */
function categoryColor(category: string): string {
  const key = category.toLowerCase();
  if (key.includes("charg")) return "var(--cyan)";
  if (key.includes("fuel")) return "var(--amber)";
  if (key.includes("oil")) return "var(--lime)";
  if (key.includes("tyre") || key.includes("tire")) return "var(--amberHot)";
  if (key.includes("repair")) return "var(--redline)";
  if (key.includes("maintenance")) return "var(--cream)";
  if (key.includes("insurance")) return "var(--creamDim)";
  if (key.includes("tax")) return "var(--mutedLight)";
  return "var(--muted)";
}

/**
 * Category split as a ring, the web counterpart of the mobile DonutChart.
 *
 * Drawn with one SVG circle per slice using stroke-dasharray rather than arc
 * paths: the geometry is a single number per slice, and the browser antialiases
 * the joins for free. `pathLength={100}` lets the dash lengths be percentages,
 * so no circumference arithmetic is needed.
 */
function CategoryDonut({ slices, size = 150, stroke = 14 }: {
  slices: { category: string; total: number }[];
  size?: number;
  stroke?: number;
}) {
  const total = slices.reduce((sum, sl) => sum + sl.total, 0);
  if (total <= 0) return null;
  const r = (size - stroke) / 2;
  let offset = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Spending by category">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--panel)" strokeWidth={stroke} />
      {slices.map((sl) => {
        const pct = (sl.total / total) * 100;
        const dash = `${pct} ${100 - pct}`;
        const el = (
          <circle
            key={sl.category}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={categoryColor(sl.category)}
            strokeWidth={stroke}
            pathLength={100}
            strokeDasharray={dash}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        );
        offset += pct;
        return el;
      })}
    </svg>
  );
}

export function SpendView({ store, car, save }: { store: CanonicalStore; car: Car; save: (s: CanonicalStore) => void }) {
  const currency = store.profile.displayCurrency;
  const ledger = deriveLedger(store, car.id);
  type EditTarget =
    | { k: "manual"; editing?: Spending }
    | { k: "fuel"; log: FuelLog }
    | { k: "tyreset"; set: TyreSet }
    | { k: "tyre"; tyre: Tyre };
  const [form, setForm] = useState<EditTarget | null>(null);
  const [openCat, setOpenCat] = useState<string | null>(null);
  const [detailCat, setDetailCat] = useState<string | null>(null);
  const [showFillups, setShowFillups] = useState(false);
  // Shared period (All / year / month) — scopes the overview and carries into the
  // category detail, so opening a category keeps the same period.
  const [period, setPeriod] = useState("all");
  const manualById = new Map(store.spendings.map((s) => [s.id, s]));
  const fuelById = new Map(store.fuelLogs.map((l) => [l.id, l]));
  const setById = new Map(store.tyreSets.map((t) => [t.id, t]));
  const tyreById = new Map(store.tyres.map((t) => [t.id, t]));

  const economy = makeEconomy(store.profile.unitSystem === "metric");
  const currentOdoKm = fuelLogsForCar(store.fuelLogs, car.id).reduce((m, l) => Math.max(m, l.odometer), 0);

  // Route a ledger entry click to the right editor.
  function openEntry(e: LedgerEntry) {
    if (e.source === "manual") { setForm({ k: "manual", editing: manualById.get(e.id) }); return; }
    if (e.source === "fuel") { const log = fuelById.get(e.id); if (log) setForm({ k: "fuel", log }); return; }
    if (e.source === "tyre" && e.id.startsWith("tyreset-")) {
      const set = setById.get(e.id.slice("tyreset-".length));
      if (set) setForm({ k: "tyreset", set });
    } else if (e.source === "tyre" && e.id.startsWith("tyre-")) {
      const tyre = tyreById.get(e.id.slice("tyre-".length));
      if (tyre) setForm({ k: "tyre", tyre });
    }
  }
  function isEditable(e: LedgerEntry): boolean {
    if (e.source === "manual" || e.source === "fuel") return true;
    if (e.source === "tyre" && e.id.startsWith("tyreset-")) return setById.has(e.id.slice("tyreset-".length));
    if (e.source === "tyre" && e.id.startsWith("tyre-")) return tyreById.has(e.id.slice("tyre-".length));
    return false;
  }

  const entriesByCat = useMemo(() => {
    const map = new Map<string, LedgerEntry[]>();
    for (const e of ledger.entries) {
      const g = map.get(e.category) ?? [];
      g.push(e);
      map.set(e.category, g);
    }
    // newest first within each category
    for (const g of map.values()) g.sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
    return map;
  }, [ledger.entries]);

  // Period-scoped overview: entries in the selected period, grouped/totalled by
  // category. The all-time `entriesByCat` above still feeds the category detail so
  // its own filter can reach any period.
  const { months: periodMonths, years: periodYears } = useMemo(
    () => extractPeriods(ledger.entries.map((e) => e.date)), [ledger.entries]);
  const scopedEntries = useMemo(
    () => filterByPeriod(ledger.entries, (e) => e.date, period), [ledger.entries, period]);
  const scopedByCat = useMemo(() => {
    const map = new Map<string, LedgerEntry[]>();
    for (const e of scopedEntries) {
      const g = map.get(e.category) ?? [];
      g.push(e);
      map.set(e.category, g);
    }
    for (const g of map.values()) g.sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
    return map;
  }, [scopedEntries]);
  const scopedCategories = useMemo(
    () => [...scopedByCat.entries()]
      .map(([category, es]) => ({ category, total: es.reduce((s, e) => s + e.amount, 0), count: es.length }))
      .sort((a, b) => b.total - a.total),
    [scopedByCat]);
  const scopedTotal = scopedEntries.reduce((s, e) => s + e.amount, 0);

  const forms = (
    <>
      {form?.k === "manual" && <SpendingForm store={store} car={car} editing={form.editing} onSave={save} onClose={() => setForm(null)} />}
      {form?.k === "fuel" && <FuelLogForm store={store} car={car} editing={form.log} onSave={save} onClose={() => setForm(null)} />}
      {form?.k === "tyreset" && <TyreSetForm store={store} car={car} editing={form.set} onSave={save} onClose={() => setForm(null)} currentOdoKm={currentOdoKm} economy={economy} />}
      {form?.k === "tyre" && <TyreForm store={store} car={car} editing={form.tyre} onSave={save} onClose={() => setForm(null)} economy={economy} />}
    </>
  );

  const renderRow = (e: LedgerEntry) => {
    const editable = isEditable(e);
    const note = e.source === "manual" ? (manualById.get(e.id)?.notes ?? null) : null;
    return (
      <div
        key={`${e.source}-${e.id}`}
        className="panel"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: 12, cursor: editable ? "pointer" : "default", opacity: editable ? 1 : 0.85 }}
        onClick={editable ? () => openEntry(e) : undefined}
      >
        <div style={{ minWidth: 0 }}>
          <div>
            <span className="mono cream" style={{ fontSize: 12 }}>{e.category}</span>
            {e.label && <span className="mono muted" style={{ fontSize: 10, marginLeft: 8 }}>{e.label}</span>}
            <span className="mono muted" style={{ fontSize: 10, marginLeft: 8 }}>{longDate(e.date)}</span>
            {!editable && <span className="mono muted" style={{ fontSize: 9, marginLeft: 8 }}>· auto</span>}
          </div>
          {note && <div className="mono muted" style={{ fontSize: 10, marginTop: 3, lineHeight: 1.4 }}>{note}</div>}
        </div>
        <div className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{e.amount.toFixed(2)} <span className="muted" style={{ fontSize: 9 }}>{currency}</span></div>
      </div>
    );
  };

  // Fuel/charge entries live on the dedicated fill-ups screen.
  if (showFillups) {
    return (
      <>
        <FillupsView store={store} car={car} save={save} onBack={() => setShowFillups(false)} />
        {forms}
      </>
    );
  }

  // Full-screen per-category detail with the shared period filter (All / year / month).
  if (detailCat) {
    return (
      <>
        <CategoryDetailView
          category={detailCat}
          entries={entriesByCat.get(detailCat) ?? []}
          currency={currency}
          period={period}
          setPeriod={setPeriod}
          onBack={() => setDetailCat(null)}
          renderRow={renderRow}
        />
        {forms}
      </>
    );
  }

  return (
    <div className="container glow">
      <div className="topbar">
        <div className="title">SPEND · <span className="car">{car.model}</span></div>
        <button className="btn ghost" style={{ padding: "8px 14px", fontSize: 11 }} onClick={() => setForm({ k: "manual" })}>+ ADD NEW SPENDING</button>
      </div>
      <div className="eyebrow amber">{period === "all" ? "LEDGER · ALL TIME" : "LEDGER · PERIOD"}</div>
      <div className="hero"><SplitFlapNumber value={round0(scopedTotal)} size="6em" /><span className="unit">{currency}</span></div>

      {/* Category split, directly under the total it breaks down — the web
          counterpart of the donut + legend the mobile apps show. */}
      {scopedCategories.length > 0 && scopedTotal > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 18, flexWrap: "wrap" }}>
          <CategoryDonut slices={scopedCategories} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1, minWidth: 160 }}>
            {scopedCategories.slice(0, 5).map((c) => (
              <div key={c.category} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: categoryColor(c.category), flex: "0 0 auto",
                }} />
                <span className="mono cream" style={{ fontSize: 11, letterSpacing: 1.2, flex: 1, minWidth: 0 }}>
                  {c.category.toUpperCase()}
                </span>
                <span className="mono muted" style={{ fontSize: 10 }}>
                  {Math.round((c.total / scopedTotal) * 100)}%
                </span>
                <span className="mono amber" style={{ fontSize: 11 }}>{round0(c.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {ledger.entries.length > 0 && (
        <PeriodFilter period={period} setPeriod={setPeriod} months={periodMonths} years={periodYears} total={ledger.entries.length} />
      )}

      <div className="divider"><span className="label">BY CATEGORY</span><span className="line" /></div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {scopedCategories.map((c) => {
          const open = openCat === c.category;
          const catEntries = scopedByCat.get(c.category) ?? [];
          const preview = catEntries.slice(0, 3);
          return (
            <div key={c.category} className="panel" style={{ padding: 0, borderColor: open ? "var(--amberHot)" : undefined, overflow: "hidden" }}>
              <div
                style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14, cursor: "pointer" }}
                onClick={() => setOpenCat(open ? null : c.category)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span className="mono muted" style={{ fontSize: 12, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}>›</span>
                  <div>
                    <div className="mono cream" style={{ fontSize: 13, fontWeight: 600 }}>{c.category}</div>
                    <div className="mono muted" style={{ fontSize: 9 }}>{c.count} {c.count === 1 ? "entry" : "entries"}</div>
                  </div>
                </div>
                <div className="mono amber" style={{ fontSize: 15 }}>{round0(c.total)} <span className="muted" style={{ fontSize: 9 }}>{currency}</span></div>
              </div>

              {open && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "0 12px 12px" }}>
                  {preview.map(renderRow)}
                  {(c.count > 3 || isFuelCategory(c.category)) && (
                    <button
                      className="linkrow"
                      style={{ marginTop: 4, justifyContent: "space-between", width: "100%" }}
                      onClick={() => (isFuelCategory(c.category) ? setShowFillups(true) : setDetailCat(c.category))}
                    >
                      <span className="mono amber" style={{ fontSize: 10, letterSpacing: 2 }}>
                        {isFuelCategory(c.category) ? `VIEW ALL ${c.category === "Charging" ? "CHARGES" : "FILL-UPS"}` : `VIEW ALL ${c.count}`}
                      </span>
                      <span className="mono muted" style={{ fontSize: 12 }}>›</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {scopedCategories.length === 0 && (
          <div className="panel mono muted" style={{ fontSize: 12 }}>
            {ledger.entries.length === 0
              ? "No spendings yet. Tap “+ ADD NEW SPENDING”."
              : "No spendings in this period. Pick another from the filter above."}
          </div>
        )}
      </div>

      {forms}
    </div>
  );
}

function CategoryDetailView({
  category, entries, currency, period, setPeriod, onBack, renderRow,
}: {
  category: string;
  entries: LedgerEntry[];
  currency: string;
  period: string;
  setPeriod: (v: string) => void;
  onBack: () => void;
  renderRow: (e: LedgerEntry) => React.ReactNode;
}) {
  const { months, years } = useMemo(() => extractPeriods(entries.map((e) => e.date)), [entries]);
  const filtered = useMemo(() => filterByPeriod(entries, (e) => e.date, period), [entries, period]);
  const total = filtered.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="container glow">
      <BackRow onClick={onBack} />
      <div className="eyebrow amber" style={{ marginTop: 4 }}>SPEND · <span className="muted">{category.toUpperCase()}</span></div>
      <div className="headline">{category}<span className="accent">.</span></div>

      {entries.length > 0 && (
        <PeriodFilter period={period} setPeriod={setPeriod} months={months} years={years} total={entries.length} />
      )}

      <div className="mono muted" style={{ fontSize: 10, marginTop: 10 }}>
        {filtered.length} {filtered.length === 1 ? "entry" : "entries"} · {round0(total)} {currency}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
        {filtered.map(renderRow)}
        {filtered.length === 0 && <div className="panel mono muted" style={{ fontSize: 12 }}>No entries.</div>}
      </div>
    </div>
  );
}
